import React, {
  useCallback,
  useState,
  useContext,
  useEffect,
  useMemo,
  Dispatch,
  SetStateAction,
} from "react";
import { AxiosError } from "axios";
import { useInfiniteQuery, useQuery } from "react-query";
import { useLocation, useParams, useRouteMatch } from "react-router-dom";

import UserContext from "contexts/userContext";
import useNavigationType from "fe-shared/src/hooks/useNavigationType";
import useEntityNavigationHandle from "fe-shared/src/hooks/useEntityNavigationHandle"
import useCancelableRequest from "fe-shared/src/hooks/useCancelableRequest"
import { ApiError } from "fe-shared/src/types/common";
import { hasPermission } from "fe-shared/src/helpers/permissions";
import { BusinessRole, PermissionLevel } from "fe-shared/src/types/domain";
import useRecycleMaterials from "fe-shared/src/hooks/useRecycleMaterials";
import {
  CorrectionRawResponse,
  Transaction,
  TransactionType,
} from "fe-shared-transactions/src/types";
import useTransactionsRequestParams from "hooks/useTransactionsRequestParams";

import {
  PERMISSIONS,
  NAVIGATION_PAGE_SIZE,
  TRANSACTIONS_NAVIGATION_QUERY_KEY,
  TRANSACTIONS_QUERY_KEY,
  CORRECTIONS_QUERY_KEY,
  TRANSACTION_LIST_INFO_QUERY_KEY,
  TRANSACTION_LINKED_TO_ADJUSTMENT,
  TRANSACTION_LINKED_TO_ORIGINAL,
} from "consts";

import TransactionDetailsView from "../TransactionDetailsView";

import * as api from "../modules/api";

interface Props {
  setLastOpenedNumber: Dispatch<SetStateAction<string | null>>;
  apiBaseUrl?: string;
}

const TransactionDetailsViewContainer = ({
  setLastOpenedNumber,
  apiBaseUrl,
}: Props) => {
  const { transactionNumber } = useParams<{ transactionNumber: string }>();
  const { search } = useLocation();
  const { path } = useRouteMatch();
  const user = useContext(UserContext);
  const { requestParams } = useTransactionsRequestParams();
  const canViewTransactionDetails = hasPermission(user?.permissions, {
    name: PERMISSIONS.transactionDetails,
    level: PermissionLevel.Read,
  });
  const returnUrl = useMemo(() => path.replace("/:transactionNumber", ""), [
    path,
  ]);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [isWithNavigation, setIsWithNavigation] = useState(searchParams.get("navigation") === "true");
  const { isDirectNavigation } = useNavigationType();

  const getTransactionCancelToken = useCancelableRequest();
  const getCorrectionsCancelToken = useCancelableRequest();
  const getTransactonExistValidationCancelToken = useCancelableRequest();

  useEffect(() => {
    if (transactionNumber) {
      setLastOpenedNumber(transactionNumber);
    }
  }, [setLastOpenedNumber, transactionNumber]);

  const navigationVerificationQueryKey = [
    transactionNumber,
    requestParams
  ];

  const fetchTransactionListInfo = useCallback(
    async (queryKey: string) => {
      const cancelToken = getTransactonExistValidationCancelToken(queryKey);
      return api.getTransactionTransactionListInfo(transactionNumber, requestParams, cancelToken);
    }, [transactionNumber, requestParams]);

  const transactionListInfo = useQuery<
    api.TransactionListInfo,
    AxiosError<ApiError>
  >(navigationVerificationQueryKey,
    (_) => fetchTransactionListInfo(TRANSACTION_LIST_INFO_QUERY_KEY), {
    enabled: isWithNavigation && isDirectNavigation,
    refetchOnWindowFocus: false,
    keepPreviousData: false
  });

  useEntityNavigationHandle(
    `${returnUrl}/${transactionNumber}`,
    searchParams,
    setIsWithNavigation,
    transactionNumber,
    transactionListInfo?.data?.currentTransactionIndex
  );

  const fetchTransaction = useCallback(
    async (queryKey: string) => {
      const cancelToken = getTransactionCancelToken(queryKey);
      return api.getTransaction(apiBaseUrl, transactionNumber, cancelToken);
    }, [apiBaseUrl, transactionNumber]);

  const transactionQueryKey = [TRANSACTIONS_QUERY_KEY, transactionNumber];
  const transaction = useQuery<Transaction, AxiosError<ApiError>>(
    transactionQueryKey,
    (_) => fetchTransaction(TRANSACTIONS_QUERY_KEY),
    {
      keepPreviousData: true,
      enabled: canViewTransactionDetails,
    }
  );
  const { linkedTransactions, transactionType } = transaction.data || {};

  const fetchCorrections = useCallback(
    async (queryKey: string) => {
      const cancelToken = getCorrectionsCancelToken(queryKey);
      return api.getCorrections(transactionNumber, cancelToken);
    }, [transactionNumber]);

  const correctionsQueryKey = [CORRECTIONS_QUERY_KEY, transactionNumber];
  const corrections = useQuery<CorrectionRawResponse, AxiosError<ApiError>>(
    correctionsQueryKey,
    (_) => fetchCorrections(CORRECTIONS_QUERY_KEY),
    {
      keepPreviousData: true,
      enabled: canViewTransactionDetails,
    }
  );

  const linkedAdjustmentNumber = useMemo(
    () =>
      linkedTransactions?.find(
        (item) => item.transactionType === TransactionType.Adjustment
      )?.transactionNumber,

    [linkedTransactions]
  );

  const linkedOriginalNumber = useMemo(
    () =>
      transactionType === TransactionType.Adjustment
        ? linkedTransactions?.[0]?.transactionNumber
        : undefined,
    [transactionType, linkedTransactions]
  );

  const linkedAdjustmentQueryKey = [
    TRANSACTIONS_QUERY_KEY,
    linkedAdjustmentNumber,
  ];

  const linkedAdjustment = useQuery<Transaction, AxiosError<ApiError>>(
    linkedAdjustmentQueryKey,
    (_) => fetchTransaction(TRANSACTION_LINKED_TO_ADJUSTMENT),
    {
      keepPreviousData: !!linkedAdjustmentNumber,
      enabled: canViewTransactionDetails && linkedAdjustmentNumber,
    }
  );

  const linkedOriginalQueryKey = [TRANSACTIONS_QUERY_KEY, linkedOriginalNumber];
  const linkedOriginal = useQuery<Transaction, AxiosError<ApiError>>(
    linkedOriginalQueryKey,
    (_) => fetchTransaction(TRANSACTION_LINKED_TO_ORIGINAL),
    {
      keepPreviousData: !!linkedOriginalNumber,
      enabled: canViewTransactionDetails && linkedOriginalNumber,
    }
  );

  const navigationTransactionsQueryKey = [
    TRANSACTIONS_NAVIGATION_QUERY_KEY,
    requestParams,
  ];

  const navigationTransactions = useInfiniteQuery<
    api.NavigationTransactions,
    AxiosError<ApiError>
  >(navigationTransactionsQueryKey,
    api.getNavigationTransactions, {
    enabled: isWithNavigation,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    getFetchMore: (lastGroup, allGroups) =>
      allGroups.length * NAVIGATION_PAGE_SIZE < lastGroup.total
        ? allGroups.length + 1
        : undefined
  });
  const recycleMaterials = useRecycleMaterials([BusinessRole.Retreader]);

  return (
    <TransactionDetailsView
      returnUrl={returnUrl}
      transaction={transaction}
      linkedOriginal={linkedOriginal}
      linkedAdjustment={linkedAdjustment}
      corrections={corrections}
      isWithNavigation={isWithNavigation}
      transactionQueryKey={transactionQueryKey}
      correctionsQueryKey={correctionsQueryKey}
      navigationTransactions={navigationTransactions}
      transactionListInfo={transactionListInfo}
      recycleMaterials={recycleMaterials}
      isFetching={transactionNumber !== transaction.data?.transactionNumber}
      isDirectNavigation={isDirectNavigation}
    />
  );
};

export default TransactionDetailsViewContainer;
